// The question card field of the DeckQuestion boards: a contentEditable `.compose` with non-editable blanks.
// The DOM is the field's state: every `input` reads the text and the blanks back from it.
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';

export const BLANK_LENGTH = 5; // a blank shows as `_____` in the game
export const MAX_BLANKS = 2;

export const partsLength = parts => parts.reduce((n, p) => n + p.length, 0) + BLANK_LENGTH * (parts.length - 1);

// Never keeps half an emoji: the server refuses a lone surrogate.
const cut = (text, n) => text.slice(0, n).replace(/[\uD800-\uDBFF]$/, '');

const isBlank = node => node.nodeType === Node.ELEMENT_NODE && node.classList.contains('blank');

const blankSpan = () => {
  const span = document.createElement('span');
  span.className = 'blank';
  span.setAttribute('contenteditable', 'false');
  return span;
};

// Text between blanks; any markup the browser added is flattened to its text, line breaks to spaces.
export const readParts = root => {
  const parts = [''];
  const walk = node => node.childNodes.forEach(child => {
    if (isBlank(child)) parts.push('');
    else if (child.nodeType === Node.TEXT_NODE) parts[parts.length - 1] += child.nodeValue.replace(/[\r\n]+/g, ' ');
    else walk(child);
  });
  walk(root);
  return parts;
};

const fill = (root, parts) => {
  root.replaceChildren();
  parts.forEach((part, i) => {
    if (i > 0) root.appendChild(blankSpan());
    if (part) root.appendChild(document.createTextNode(part));
  });
};

const QuestionField = forwardRef(function QuestionField({ initial, maxLength, locked, editing, onChange, ...aria }, ref) {
  const root = useRef(null);
  const emit = () => onChange(readParts(root.current));

  // Layout effect: a passive one can run after the first paste or keystroke and overwrite it.
  useLayoutEffect(() => {
    if (root.current) fill(root.current, initial); // the parent remounts the field to load other text
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A selection inside the field, or a caret at its end.
  const range = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && root.current.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      return selection.getRangeAt(0);
    }
    const end = document.createRange();
    end.selectNodeContents(root.current);
    end.collapse(false);
    return end;
  };

  const selectedLength = r => {
    const box = document.createElement('div');
    box.appendChild(r.cloneContents());
    return partsLength(readParts(box));
  };

  const insert = node => {
    const r = range();
    r.deleteContents();
    r.insertNode(node);
    r.setStartAfter(node);
    r.collapse(true);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(r);
    }
    emit();
  };

  useImperativeHandle(ref, () => ({
    insertBlank: () => {
      const parts = readParts(root.current);
      if (parts.length - 1 >= MAX_BLANKS || partsLength(parts) + BLANK_LENGTH > maxLength) return;
      insert(blankSpan());
    },
  }));

  const insertText = text => {
    const r = range();
    const room = maxLength - partsLength(readParts(root.current)) + selectedLength(r);
    const clean = cut(text.replace(/[\r\n]+/g, ' '), Math.max(room, 0));
    if (clean) insert(document.createTextNode(clean));
  };

  // Native `beforeinput` (React's onBeforeInput is a keypress polyfill without inputType).
  useEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    const refuse = e => {
      if (e.inputType === 'insertParagraph' || e.inputType === 'insertLineBreak') {
        e.preventDefault();
        return;
      }
      if (!e.inputType?.startsWith('insert')) return; // deletions and history pass; onInput checks the blanks
      const data = e.data ?? e.dataTransfer?.getData('text/plain') ?? '';
      if (partsLength(readParts(el)) - selectedLength(range()) + data.length > maxLength) e.preventDefault();
    };
    el.addEventListener('beforeinput', refuse);
    return () => el.removeEventListener('beforeinput', refuse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxLength]);

  const plain = getText => e => {
    e.preventDefault();
    insertText(getText(e) ?? '');
  };

  // Input `beforeinput` cannot refuse (IME composition, undo): drop a third blank and trim text past
  // maxLength from the end, then put the caret at the end.
  const check = () => {
    let parts = readParts(root.current);
    const original = parts.join('\u0000');
    if (parts.length - 1 > MAX_BLANKS) parts = parts.slice(0, MAX_BLANKS).concat(parts.slice(MAX_BLANKS).join(''));
    for (let i = parts.length - 1; i >= 0 && partsLength(parts) > maxLength; i -= 1) {
      parts[i] = cut(parts[i], Math.max(parts[i].length - (partsLength(parts) - maxLength), 0));
    }
    if (parts.join('\u0000') !== original || readParts(root.current).length !== parts.length) {
      fill(root.current, parts);
      const end = document.createRange();
      end.selectNodeContents(root.current);
      end.collapse(false);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(end);
      }
    }
    emit();
  };

  const onInput = e => {
    if (!e.nativeEvent?.isComposing) check();
  };

  if (locked) {
    return (
      <div className="compose" role="textbox" aria-multiline="true" {...aria} aria-disabled="true"
        style={{ background: 'var(--disabled)', color: 'var(--disabled-ink)', borderStyle: 'dashed', boxShadow: 'none' }}></div>
    );
  }
  return (
    <div ref={root} className={editing ? 'compose input-focus' : 'compose'} role="textbox" aria-multiline="true" {...aria}
      contentEditable suppressContentEditableWarning
      onInput={onInput} onCompositionEnd={check}
      onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
      onPaste={plain(e => e.clipboardData?.getData('text/plain'))}
      onDrop={plain(e => e.dataTransfer?.getData('text/plain'))}></div>
  );
});

export default QuestionField;
